import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { HelpCircle, Send, Loader2, CheckCircle } from 'lucide-react';
import { supportAPI } from '@/lib/api';

export function SupportTicketButton({ variant = 'outline', size = 'sm', className = '', contextLabel = '' }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketKey, setTicketKey] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    ticket_type: 'support',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const result = await supportAPI.createTicket(formData);
      setTicketKey(result.external_ticket_key);
      setSubmitted(true);
      toast.success('Support ticket created!', {
        description: result.external_ticket_key ? `Ticket: ${result.external_ticket_key}` : undefined,
      });
    } catch (error) {
      toast.error('Failed to create support ticket', {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset form after dialog animation completes
    setTimeout(() => {
      setSubmitted(false);
      setTicketKey(null);
      setFormData({
        subject: '',
        description: '',
        priority: 'medium',
        ticket_type: 'support',
      });
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => isOpen ? setOpen(true) : handleClose()}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className} data-testid="create-support-ticket-btn">
          <HelpCircle className="h-4 w-4 mr-2" />
          Get Support
          {contextLabel && (
            <span className="ml-2 hidden border-l border-border pl-2 font-mono text-[11px] font-normal text-muted-foreground md:inline">
              {contextLabel}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-success">
                <CheckCircle className="h-5 w-5" />
                Ticket Created
              </DialogTitle>
              <DialogDescription>
                Your support request has been submitted.
              </DialogDescription>
            </DialogHeader>
            <div className="py-6 text-center space-y-4">
              {ticketKey && (
                <div className="p-4 bg-secondary/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Ticket Reference</p>
                  <p className="text-lg font-mono font-semibold">{ticketKey}</p>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Our support team will review your request and respond as soon as possible.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Close</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
              <DialogDescription>
                Describe your issue and our team will assist you.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {contextLabel && (
                <div className="rounded-md border border-border bg-secondary/30 px-3 py-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Workspace ID</p>
                  <p className="mt-1 font-mono text-sm text-foreground">{contextLabel}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of your issue"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  data-testid="ticket-subject-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="ticket-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.ticket_type}
                    onValueChange={(value) => setFormData({ ...formData, ticket_type: value })}
                  >
                    <SelectTrigger data-testid="ticket-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="support">General Support</SelectItem>
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="billing">Billing Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Provide details about your issue..."
                  rows={5}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="ticket-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-ticket-btn">
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Submit Ticket
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
